/**
 * Settings Scene - Redesigned
 *
 * Features:
 * - Clean dark background matching main menu
 * - GlassButton components for consistency
 * - Scrollable content for all screen sizes
 * - Live sensor readings
 * - Phone animation visualization
 * - Volume controls with test buttons
 */

import Phaser from 'phaser';
import { SettingsManager, MovementType } from '../systems/SettingsManager';
import { SoundManager } from '../systems/SoundManager';
import { HapticManager } from '../systems/HapticManager';
import { VictoryHistoryManager } from '../systems/VictoryHistoryManager';
import { GlassButton } from '../ui/components/GlassButton';
import { MenuParticleSystem } from '../ui/effects/MenuParticleSystem';
import { UI_COLORS, TYPOGRAPHY, Z_INDEX } from '../config/designSystem';

export class SettingsScene extends Phaser.Scene {
  private invertButton!: Phaser.GameObjects.Text;
  private hapticsButton!: Phaser.GameObjects.Text;
  private disableLeaderboardButton!: Phaser.GameObjects.Text;
  private showConsoleButton!: Phaser.GameObjects.Text;
  private showPlatformIdsButton!: Phaser.GameObjects.Text;
  private showFpsCounterButton!: Phaser.GameObjects.Text;
  private enableTeleportButton!: Phaser.GameObjects.Text;

  // Radio button circles
  private rollRadio!: Phaser.GameObjects.Arc;
  private pitchRadio!: Phaser.GameObjects.Arc;

  // Sensor reading displays
  private rollReading!: Phaser.GameObjects.Text;
  private pitchReading!: Phaser.GameObjects.Text;

  // Animation container
  private phoneAnimation!: Phaser.GameObjects.Container;

  // Scrollable content container
  private contentContainer!: Phaser.GameObjects.Container;
  private isDragging: boolean = false;
  private dragStartY: number = 0;
  private contentStartY: number = 0;
  private minScrollY: number = 0;
  private maxScrollY: number = 0;
  private totalDragDistance: number = 0; // Track total drag distance to distinguish scroll from tap
  private lastPointerY: number = 0;

  // Sensor values
  private orientationHandler: ((event: DeviceOrientationEvent) => void) | null = null;
  private rollValue: number = 0;
  private pitchValue: number = 0;
  private calibrationRoll: number = 0;
  private calibrationPitch: number = 0;

  // Test sound states
  private isMusicTestPlaying: boolean = false;

  // Developer settings visibility
  private developerContainer!: Phaser.GameObjects.Container;
  private tapCount: number = 0;
  private tapTimer: number = 0;
  private readonly TAP_THRESHOLD: number = 7; // Number of taps required
  private readonly TAP_TIMEOUT: number = 3000; // Time window in ms
  private settingsTitle!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'SettingsScene' });
  }

  create(): void {
    const centerX = this.cameras.main.centerX;
    const height = this.cameras.main.height;

    // Dark background matching main menu
    this.cameras.main.setBackgroundColor('#0A0E27');

    // Add floating particles (managed by Phaser scene graph)
    new MenuParticleSystem(this);

    // Create scrollable content container
    this.contentContainer = this.add.container(0, 0);
    this.contentContainer.setDepth(Z_INDEX.uiContent);

    // Title
    this.settingsTitle = this.add.text(centerX, 40, 'SETTINGS', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.title}px`,
      color: UI_COLORS.primary,
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 4,
      resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
    });
    this.settingsTitle.setOrigin(0.5);
    this.settingsTitle.setInteractive({ useHandCursor: false });
    this.settingsTitle.on('pointerdown', () => this.handleTitleTap());
    this.contentContainer.add(this.settingsTitle);

    // Section: Movement Type
    this.createMovementSection(centerX, 140);

    // Section: Invert Direction (adjusted for new spacing)
    this.createInvertSection(centerX, 640);

    // Section: Haptic Feedback (new section)
    this.createHapticsSection(centerX, 810);

    // Section: Volume Controls (adjusted for new spacing)
    this.createVolumeSection(centerX, 980);

    // Section: FPS Counter (available to all users)
    this.createFpsCounterSection(centerX, 1300);

    // Section: Developer Settings
    this.createDeveloperSection(centerX, 1500);

    // Calculate content height and set up scrolling (increased for FPS counter + developer sections + bottom padding)
    const contentHeight = 2550; // Increased to accommodate FPS counter + 5 developer settings + bottom padding
    this.maxScrollY = 0;
    this.minScrollY = Math.min(0, height - contentHeight - 150); // Extra padding to ensure back button doesn't overlay

    // Setup scroll/drag
    this.setupScrolling();

    // Fixed back button (not in scrollable container)
    const backButton = new GlassButton(this, centerX, height - 50, {
      text: '← BACK',
      fontSize: 20,
      width: 180,
      height: 52,
      style: 'secondary',
      onClick: () => {
        this.cleanup();
        this.scene.start('MainMenuScene');
      },
    });
    backButton.setDepth(Z_INDEX.modal);
    backButton.setScrollFactor(0);

    // Setup gyroscope listener
    this.setupGyroscope();

    // Update radio buttons and animation
    this.updateRadioButtons();
    this.updateAnimation();
  }

  /**
   * Create movement type selection section
   */
  private createMovementSection(centerX: number, startY: number): void {
    // Section title
    const sectionTitle = this.add.text(centerX, startY, 'CONTROL METHOD', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.subheading}px`,
      color: '#FFFFFF', // White for readability
      fontStyle: 'bold',
      resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
    });
    sectionTitle.setOrigin(0.5);
    this.contentContainer.add(sectionTitle);

    // Animation area background (moved down to avoid title overlap)
    const animBg = this.add.rectangle(centerX, startY + 90, 320, 140, 0x1a1a2a, 0.6);
    animBg.setStrokeStyle(2, parseInt(UI_COLORS.primary.replace('#', '0x')), 0.3);
    this.contentContainer.add(animBg);

    // Create phone animation container (moved down)
    this.phoneAnimation = this.add.container(centerX, startY + 90);
    this.contentContainer.add(this.phoneAnimation);

    // Instruction text (moved down)
    const instruction = this.add.text(centerX, startY + 170, 'Move your device like this:', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.caption}px`,
      color: '#9CA3AF',
      resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
    });
    instruction.setOrigin(0.5);
    this.contentContainer.add(instruction);

    // Radio options (moved down)
    const optionsStartY = startY + 205;
    const rowHeight = 60;

    // Roll (Gamma)
    this.createRadioOption(
      centerX,
      optionsStartY,
      'Roll (tilt side-to-side)',
      MovementType.GAMMA,
      (radio, reading) => {
        this.rollRadio = radio;
        this.rollReading = reading;
      }
    );

    // Pitch (Beta)
    this.createRadioOption(
      centerX,
      optionsStartY + rowHeight,
      'Pitch (tilt forward-back)',
      MovementType.BETA,
      (radio, reading) => {
        this.pitchRadio = radio;
        this.pitchReading = reading;
      }
    );

    // Calibration button (with extra spacing below)
    const calibrateButton = new GlassButton(this, centerX, optionsStartY + rowHeight * 2 + 40, {
      text: '🎯 CALIBRATE (Set to 0°)',
      fontSize: 16,
      width: 240,
      height: 48,
      style: 'primary',
      onClick: () => this.calibrate(),
    });
    this.contentContainer.add(calibrateButton);
  }

  /**
   * Create invert direction section
   */
  private createInvertSection(centerX: number, y: number): void {
    // Section background with proper padding
    const sectionBg = this.add.rectangle(centerX, y + 45, 320, 130, 0x1a1a2a, 0.6);
    sectionBg.setStrokeStyle(2, parseInt(UI_COLORS.primary.replace('#', '0x')), 0.3);
    this.contentContainer.add(sectionBg);

    // Title (20px padding from top)
    const sectionTitle = this.add.text(centerX, y, 'Invert Direction', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.body}px`,
      color: '#D1D5DB',
      fontStyle: 'bold',
      resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
    });
    sectionTitle.setOrigin(0.5);
    this.contentContainer.add(sectionTitle);

    // Toggle button (20px spacing after title)
    this.invertButton = this.createToggleButton(centerX, y + 40, this.getInvertText(), () =>
      this.toggleInvert()
    );
    this.contentContainer.add(this.invertButton);

    // Description (15px spacing after toggle)
    const desc = this.add.text(centerX, y + 75, 'Reverses the tilt direction', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.tiny}px`,
      color: '#6B7280',
      resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
    });
    desc.setOrigin(0.5);
    this.contentContainer.add(desc);
  }

  /**
   * Create haptic feedback section
   */
  private createHapticsSection(centerX: number, y: number): void {
    // Section background with proper padding
    const sectionBg = this.add.rectangle(centerX, y + 45, 320, 130, 0x1a1a2a, 0.6);
    sectionBg.setStrokeStyle(2, parseInt(UI_COLORS.primary.replace('#', '0x')), 0.3);
    this.contentContainer.add(sectionBg);

    // Title (20px padding from top)
    const sectionTitle = this.add.text(centerX, y, 'Haptic Feedback', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.body}px`,
      color: '#D1D5DB',
      fontStyle: 'bold',
      resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
    });
    sectionTitle.setOrigin(0.5);
    this.contentContainer.add(sectionTitle);

    // Toggle button (20px spacing after title)
    this.hapticsButton = this.createToggleButton(centerX, y + 40, this.getHapticsText(), () =>
      this.toggleHaptics()
    );
    this.contentContainer.add(this.hapticsButton);

    // Description (15px spacing after toggle)
    const desc = this.add.text(centerX, y + 75, 'Vibration feedback for actions', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.tiny}px`,
      color: '#6B7280',
      resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
    });
    desc.setOrigin(0.5);
    this.contentContainer.add(desc);
  }

  /**
   * Create volume controls section
   */
  private createVolumeSection(centerX: number, startY: number): void {
    // Section background with proper padding
    const sectionBg = this.add.rectangle(centerX, startY + 138, 320, 316, 0x1a1a2a, 0.6);
    sectionBg.setStrokeStyle(2, parseInt(UI_COLORS.primary.replace('#', '0x')), 0.3);
    this.contentContainer.add(sectionBg);

    // Section title (20px padding from top)
    const sectionTitle = this.add.text(centerX, startY, 'VOLUME', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.subheading}px`,
      color: '#FFFFFF', // White for readability
      fontStyle: 'bold',
      resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
    });
    sectionTitle.setOrigin(0.5);
    this.contentContainer.add(sectionTitle);

    // SFX Volume (20px spacing after title)
    this.createVolumeSlider(
      centerX,
      startY + 50,
      'SFX',
      SettingsManager.getVolumeSfx(),
      (value: number) => {
        SettingsManager.setVolumeSfx(value);
        SoundManager.updateAllVolumes();
      },
      () => SoundManager.playJump(0.5)
    );

    // Fall Sounds Volume (spacing between sliders)
    this.createVolumeSlider(
      centerX,
      startY + 135,
      'Fall Sounds',
      SettingsManager.getVolumeFall(),
      (value: number) => {
        SettingsManager.setVolumeFall(value);
        SoundManager.updateAllVolumes();
      },
      () => SoundManager.playFallSound()
    );

    // Music Volume (spacing between sliders, 20px padding at bottom)
    this.createVolumeSlider(
      centerX,
      startY + 220,
      'Music',
      SettingsManager.getVolumeMusic(),
      (value: number) => {
        SettingsManager.setVolumeMusic(value);
        SoundManager.updateAllVolumes();
      },
      () => this.toggleMusicTest(),
      true
    );
  }

  /**
   * Create a radio option for movement type
   */
  private createRadioOption(
    centerX: number,
    y: number,
    label: string,
    type: MovementType,
    setRefs: (radio: Phaser.GameObjects.Arc, reading: Phaser.GameObjects.Text) => void
  ): void {
    const width = 320;

    // Clickable background
    const bg = this.add.rectangle(centerX, y, width, 55, 0x2a2a3a, 0.3);
    bg.setStrokeStyle(1, 0x4a4a5a, 0.5);
    bg.setInteractive({ useHandCursor: true });

    // Radio button circle
    const radioX = centerX - width / 2 + 25;
    const radio = this.add.circle(radioX, y, 8, 0x666666);
    radio.setStrokeStyle(2, 0xffffff, 0.8);

    // Label
    const labelText = this.add.text(radioX + 20, y, label, {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.caption}px`,
      color: '#FFFFFF',
      resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
    });
    labelText.setOrigin(0, 0.5);

    // Sensor reading - bright green color
    const readingX = centerX + width / 2 - 15;
    const readingText = this.add.text(readingX, y, '0.0°', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.caption}px`,
      color: '#10B981', // Bright green
      resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
    });
    readingText.setOrigin(1, 0.5);
    readingText.setDepth(Z_INDEX.uiContent);

    // Click handler - only trigger if not scrolling
    bg.on('pointerup', () => {
      if (!this.wasScroll()) {
        this.selectMovementType(type);
      }
    });

    bg.on('pointerover', () => {
      bg.setFillStyle(0x3a3a4a, 0.5);
    });

    bg.on('pointerout', () => {
      bg.setFillStyle(0x2a2a3a, 0.3);
    });

    // Store references
    setRefs(radio, readingText);

    // Add all to content container
    this.contentContainer.add([bg, radio, labelText, readingText]);
  }

  /**
   * Update phone animation based on selected movement type
   */
  private updateAnimation(): void {
    // Clear existing animation
    this.phoneAnimation.removeAll(true);
    this.tweens.killTweensOf(this.phoneAnimation);

    const type = SettingsManager.getMovementType();

    // Phone body (portrait rectangle)
    const phone = this.add.rectangle(0, 0, 50, 90, 0xffffff);
    phone.setStrokeStyle(3, 0xcccccc);

    // Screen (inner rectangle)
    const screen = this.add.rectangle(0, -5, 40, 70, 0x1a1a2a);

    // Camera notch at top
    const notch = this.add.rectangle(0, -40, 18, 4, 0x888888);

    // Home indicator at bottom
    const homeIndicator = this.add.rectangle(0, 38, 25, 3, 0x888888);

    this.phoneAnimation.add([phone, screen, notch, homeIndicator]);

    // Create animation based on type
    if (type === MovementType.GAMMA) {
      // Roll - tilt side-to-side
      const arrowLeft = this.add.text(-65, -5, '←', {
        fontSize: '36px',
        color: UI_COLORS.primary,
        resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
      });
      const arrowRight = this.add.text(40, -5, '→', {
        fontSize: '36px',
        color: UI_COLORS.primary,
        resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
      });
      this.phoneAnimation.add([arrowLeft, arrowRight]);

      this.tweens.add({
        targets: this.phoneAnimation,
        angle: { from: -25, to: 25 },
        duration: 2000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });

      this.tweens.add({
        targets: [arrowLeft, arrowRight],
        alpha: { from: 0.4, to: 1 },
        scale: { from: 0.8, to: 1.1 },
        duration: 2000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    } else if (type === MovementType.BETA) {
      // Pitch - tilt forward-back
      const arrowUp = this.add.text(-15, -75, '↑', {
        fontSize: '36px',
        color: UI_COLORS.primary,
        resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
      });
      const arrowDown = this.add.text(-15, 60, '↓', {
        fontSize: '36px',
        color: UI_COLORS.primary,
        resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
      });
      this.phoneAnimation.add([arrowUp, arrowDown]);

      this.tweens.add({
        targets: this.phoneAnimation,
        scaleY: { from: 0.75, to: 1.2 },
        y: { from: this.phoneAnimation.y - 5, to: this.phoneAnimation.y + 5 },
        duration: 2000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });

      this.tweens.add({
        targets: [arrowUp, arrowDown],
        alpha: { from: 0.4, to: 1 },
        scale: { from: 0.8, to: 1.1 },
        duration: 2000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  /**
   * Setup scrolling behavior
   */
  private setupScrolling(): void {
    const { width, height } = this.cameras.main;

    // Create invisible full-screen overlay for drag detection
    const dragArea = this.add.rectangle(0, 0, width, height, 0x000000, 0.01);
    dragArea.setOrigin(0, 0);
    dragArea.setScrollFactor(0);
    dragArea.setDepth(Z_INDEX.uiBackground);
    dragArea.setInteractive();

    dragArea.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.isDragging = true;
      this.dragStartY = pointer.y;
      this.lastPointerY = pointer.y;
      this.contentStartY = this.contentContainer.y;
      this.totalDragDistance = 0; // Reset drag distance
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.isDragging) {
        // Track total drag distance
        this.totalDragDistance += Math.abs(pointer.y - this.lastPointerY);
        this.lastPointerY = pointer.y;

        const deltaY = pointer.y - this.dragStartY;
        const newY = this.contentStartY + deltaY;
        this.contentContainer.y = Phaser.Math.Clamp(newY, this.minScrollY, this.maxScrollY);
      }
    });

    this.input.on('pointerup', () => {
      this.isDragging = false;
      // Reset drag distance after a short delay to allow wasScroll() to be checked first
      // This ensures toggle buttons can check if the action was a scroll before we reset
      this.time.delayedCall(50, () => {
        this.totalDragDistance = 0;
      });
    });
  }

  /**
   * Check if the last pointer action was a scroll (not a tap)
   * A tap should have minimal drag distance (< 10 pixels)
   */
  public wasScroll(): boolean {
    return this.totalDragDistance > 10;
  }

  /**
   * Setup gyroscope/device orientation listener
   */
  private setupGyroscope(): void {
    if (!window.DeviceOrientationEvent) {
      console.log('📱 DeviceOrientation not supported');
      return;
    }

    this.orientationHandler = (event: DeviceOrientationEvent) => {
      this.handleOrientation(event);
    };
    window.addEventListener('deviceorientation', this.orientationHandler);
  }

  /**
   * Handle device orientation events
   */
  private handleOrientation(event: DeviceOrientationEvent): void {
    // Get raw values and apply calibration
    if (event.gamma !== null && event.gamma !== undefined) {
      this.rollValue = event.gamma - this.calibrationRoll;
    }
    if (event.beta !== null && event.beta !== undefined) {
      this.pitchValue = event.beta - 90 - this.calibrationPitch;
    }
  }

  /**
   * Calibrate sensors to current position
   */
  private calibrate(): void {
    // Set current position as new baseline (0°)
    this.calibrationRoll = this.rollValue + this.calibrationRoll;
    this.calibrationPitch = this.pitchValue + this.calibrationPitch;

    // Reset displayed values to 0
    this.rollValue = 0;
    this.pitchValue = 0;

    console.log('📱 Sensors calibrated to 0°');
  }

  /**
   * Update sensor readings display
   */
  update(): void {
    if (this.rollReading) {
      this.rollReading.setText(`${this.rollValue.toFixed(1)}°`);
    }
    if (this.pitchReading) {
      this.pitchReading.setText(`${this.pitchValue.toFixed(1)}°`);
    }
  }

  /**
   * Select movement type
   */
  private selectMovementType(type: MovementType): void {
    SettingsManager.setMovementType(type);
    this.updateRadioButtons();
    this.updateAnimation();
  }

  /**
   * Update radio button states
   */
  private updateRadioButtons(): void {
    const currentType = SettingsManager.getMovementType();

    // Update Roll radio
    if (currentType === MovementType.GAMMA) {
      this.rollRadio.setFillStyle(parseInt(UI_COLORS.accent.replace('#', '0x')));
    } else {
      this.rollRadio.setFillStyle(0x666666);
    }

    // Update Pitch radio
    if (currentType === MovementType.BETA) {
      this.pitchRadio.setFillStyle(parseInt(UI_COLORS.accent.replace('#', '0x')));
    } else {
      this.pitchRadio.setFillStyle(0x666666);
    }
  }

  /**
   * Toggle invert direction
   */
  private toggleInvert(): void {
    SettingsManager.toggleInvertDirection();
    this.invertButton.setText(this.getInvertText());
  }

  /**
   * Get invert button text
   */
  private getInvertText(): string {
    const inverted = SettingsManager.getInvertDirection();
    return inverted ? 'ON' : 'OFF';
  }

  /**
   * Toggle haptic feedback
   */
  private toggleHaptics(): void {
    HapticManager.toggle();
    this.hapticsButton.setText(this.getHapticsText());
  }

  /**
   * Get haptics button text
   */
  private getHapticsText(): string {
    const enabled = HapticManager.isEnabled();
    return enabled ? 'ON' : 'OFF';
  }

  /**
   * Create toggle button for invert
   */
  private createToggleButton(
    x: number,
    y: number,
    text: string,
    onClick: () => void
  ): Phaser.GameObjects.Text {
    const button = this.add.text(x, y, text, {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.body}px`,
      color: '#FFFFFF',
      backgroundColor: '#0066cc',
      padding: { x: 30, y: 12 },
      resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
    });
    button.setOrigin(0.5);
    button.setInteractive({ useHandCursor: true });

    button.on('pointerover', () => {
      button.setStyle({ backgroundColor: '#0088ff' });
    });

    button.on('pointerout', () => {
      button.setStyle({ backgroundColor: '#0066cc' });
    });

    button.on('pointerdown', () => {
      button.setStyle({ backgroundColor: '#004499' });
    });

    button.on('pointerup', () => {
      button.setStyle({ backgroundColor: '#0088ff' });
      // Only trigger if not scrolling
      if (!this.wasScroll()) {
        onClick();
      }
    });

    return button;
  }

  /**
   * Create volume slider with test button
   */
  private createVolumeSlider(
    centerX: number,
    y: number,
    label: string,
    initialValue: number,
    onChange: (value: number) => void,
    onTest?: () => void,
    isMusic: boolean = false
  ): void {
    const sliderWidth = 240;
    const sliderY = y + 25;

    // Label above slider - centered and white
    const labelText = this.add.text(centerX, y - 10, label, {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.caption}px`,
      color: '#FFFFFF', // White for readability
      resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
    });
    labelText.setOrigin(0.5, 0.5);
    this.contentContainer.add(labelText);

    // Percentage display - bright green, right aligned
    const percentText = this.add.text(
      centerX + sliderWidth / 2 + 10,
      sliderY,
      `${Math.round(initialValue * 100)}%`,
      {
        fontFamily: TYPOGRAPHY.fonts.josefin,
        fontSize: `${TYPOGRAPHY.sizes.caption}px`,
        color: '#10B981', // Bright green
        resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
      }
    );
    percentText.setOrigin(0, 0.5);
    this.contentContainer.add(percentText);

    // Slider track
    const track = this.add.rectangle(centerX, sliderY, sliderWidth, 8, 0x4a4a5a);
    this.contentContainer.add(track);

    // Slider fill
    const fill = this.add.rectangle(
      centerX - sliderWidth / 2,
      sliderY,
      sliderWidth * initialValue,
      8,
      0x10b981 // Bright green
    );
    fill.setOrigin(0, 0.5);
    this.contentContainer.add(fill);

    // Slider handle
    const handle = this.add.circle(
      centerX - sliderWidth / 2 + sliderWidth * initialValue,
      sliderY,
      12,
      0xffffff
    );
    handle.setStrokeStyle(3, 0x10b981); // Bright green
    handle.setInteractive({ useHandCursor: true, draggable: true });
    this.contentContainer.add(handle);

    // Drag handling
    this.input.setDraggable(handle);

    handle.on('drag', (_pointer: Phaser.Input.Pointer, dragX: number) => {
      const minX = centerX - sliderWidth / 2;
      const maxX = centerX + sliderWidth / 2;
      const clampedX = Phaser.Math.Clamp(dragX, minX, maxX);

      handle.x = clampedX;
      const value = (clampedX - minX) / sliderWidth;

      fill.width = sliderWidth * value;
      percentText.setText(`${Math.round(value * 100)}%`);

      onChange(value);
    });

    // Click on track to jump
    track.setInteractive({ useHandCursor: true });
    track.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      const minX = centerX - sliderWidth / 2;
      const maxX = centerX + sliderWidth / 2;
      const clampedX = Phaser.Math.Clamp(pointer.x, minX, maxX);

      handle.x = clampedX;
      const value = (clampedX - minX) / sliderWidth;

      fill.width = sliderWidth * value;
      percentText.setText(`${Math.round(value * 100)}%`);

      onChange(value);
    });

    // Test button - positioned below slider
    if (onTest) {
      const buttonText = isMusic ? (this.isMusicTestPlaying ? '⏸' : '▶') : '▶';
      const testBtn = this.add.text(centerX, sliderY + 30, buttonText, {
        fontFamily: TYPOGRAPHY.fonts.josefin,
        fontSize: '16px',
        color: '#FFFFFF',
        backgroundColor: '#0066cc',
        padding: { x: 10, y: 5 },
        resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
      });
      testBtn.setOrigin(0.5);
      testBtn.setInteractive({ useHandCursor: true });

      testBtn.on('pointerover', () => {
        testBtn.setStyle({ backgroundColor: '#0088ff' });
      });

      testBtn.on('pointerout', () => {
        testBtn.setStyle({ backgroundColor: '#0066cc' });
      });

      testBtn.on('pointerdown', () => {
        testBtn.setStyle({ backgroundColor: '#004499' });
      });

      testBtn.on('pointerup', () => {
        testBtn.setStyle({ backgroundColor: '#0088ff' });

        // Only trigger if not scrolling
        if (!this.wasScroll()) {
          onTest();

          // Update music button text
          if (isMusic) {
            if (this.isMusicTestPlaying) {
              testBtn.setText('⏸');
            } else {
              testBtn.setText('▶');
            }
          }
        }
      });

      this.contentContainer.add(testBtn);
    }
  }

  /**
   * Toggle music test playback
   */
  private toggleMusicTest(): void {
    if (this.isMusicTestPlaying) {
      SoundManager.stopMusic();
      this.isMusicTestPlaying = false;
    } else {
      SoundManager.startMusic();
      this.isMusicTestPlaying = true;
    }
  }

  /**
   * Create FPS counter section (available to all users)
   */
  private createFpsCounterSection(centerX: number, y: number): void {
    // Section background with proper padding
    const sectionBg = this.add.rectangle(centerX, y + 45, 320, 130, 0x1a1a2a, 0.6);
    sectionBg.setStrokeStyle(2, parseInt(UI_COLORS.primary.replace('#', '0x')), 0.3);
    this.contentContainer.add(sectionBg);

    // Title (20px padding from top)
    const sectionTitle = this.add.text(centerX, y, 'Show FPS Counter', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.body}px`,
      color: '#D1D5DB',
      fontStyle: 'bold',
      resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
    });
    sectionTitle.setOrigin(0.5);
    this.contentContainer.add(sectionTitle);

    // Toggle button (20px spacing after title)
    this.showFpsCounterButton = this.createToggleButton(
      centerX,
      y + 40,
      this.getShowFpsCounterText(),
      () => this.toggleShowFpsCounter()
    );
    this.contentContainer.add(this.showFpsCounterButton);

    // Description (15px spacing after toggle)
    const desc = this.add.text(centerX, y + 75, 'Display frames per second during gameplay', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.tiny}px`,
      color: '#6B7280',
      resolution: Math.max(2, window.devicePixelRatio) * 2, // Ultra high DPI: 2x multiplier
    });
    desc.setOrigin(0.5);
    this.contentContainer.add(desc);
  }

  /**
   * Developer settings section
   */
  private createDeveloperSection(centerX: number, y: number): void {
    // Create a container for all developer settings elements
    this.developerContainer = this.add.container(0, 0);
    this.contentContainer.add(this.developerContainer);

    // Section title
    const sectionTitle = this.add.text(centerX, y, 'DEVELOPER', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.subheading}px`,
      color: '#FFFFFF',
      fontStyle: 'bold',
      resolution: Math.max(3, window.devicePixelRatio), // High DPI for sharp text on mobile
    });
    sectionTitle.setOrigin(0.5);
    this.developerContainer.add(sectionTitle);

    // Disable Leaderboard Submission section
    this.createLeaderboardToggleSection(centerX, y + 60);

    // Show JS Console section
    this.createConsoleToggleSection(centerX, y + 230);

    // Show Platform IDs section
    this.createPlatformIdsToggleSection(centerX, y + 400);

    // Enable Teleport section
    this.createEnableTeleportSection(centerX, y + 570);

    // Victory Entries Testing section
    this.createVictoryTestingSection(centerX, y + 740);

    // Set initial visibility based on stored state
    this.developerContainer.setVisible(SettingsManager.getDevSettingsVisible());
  }

  /**
   * Create leaderboard toggle section
   */
  private createLeaderboardToggleSection(centerX: number, y: number): void {
    // Section background with proper padding
    const sectionBg = this.add.rectangle(centerX, y + 45, 320, 130, 0x1a1a2a, 0.6);
    sectionBg.setStrokeStyle(2, parseInt(UI_COLORS.primary.replace('#', '0x')), 0.3);
    this.developerContainer.add(sectionBg);

    // Title
    const sectionTitle = this.add.text(centerX, y, 'Disable Leaderboard', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.body}px`,
      color: '#D1D5DB',
      fontStyle: 'bold',
      resolution: Math.max(3, window.devicePixelRatio), // High DPI for sharp text on mobile
    });
    sectionTitle.setOrigin(0.5);
    this.developerContainer.add(sectionTitle);

    // Toggle button
    this.disableLeaderboardButton = this.createToggleButton(
      centerX,
      y + 40,
      this.getDisableLeaderboardText(),
      () => this.toggleDisableLeaderboard()
    );
    this.developerContainer.add(this.disableLeaderboardButton);

    // Description
    const desc = this.add.text(centerX, y + 75, 'Prevent score submission while testing', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.tiny}px`,
      color: '#6B7280',
      resolution: Math.max(3, window.devicePixelRatio), // High DPI for sharp text on mobile
    });
    desc.setOrigin(0.5);
    this.developerContainer.add(desc);
  }

  /**
   * Create console toggle section
   */
  private createConsoleToggleSection(centerX: number, y: number): void {
    // Section background with proper padding
    const sectionBg = this.add.rectangle(centerX, y + 45, 320, 130, 0x1a1a2a, 0.6);
    sectionBg.setStrokeStyle(2, parseInt(UI_COLORS.primary.replace('#', '0x')), 0.3);
    this.developerContainer.add(sectionBg);

    // Title
    const sectionTitle = this.add.text(centerX, y, 'Show JS Console', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.body}px`,
      color: '#D1D5DB',
      fontStyle: 'bold',
      resolution: Math.max(3, window.devicePixelRatio), // High DPI for sharp text on mobile
    });
    sectionTitle.setOrigin(0.5);
    this.developerContainer.add(sectionTitle);

    // Toggle button
    this.showConsoleButton = this.createToggleButton(
      centerX,
      y + 40,
      this.getShowConsoleText(),
      () => this.toggleShowConsole()
    );
    this.developerContainer.add(this.showConsoleButton);

    // Description
    const desc = this.add.text(centerX, y + 75, 'Display in-game console for debugging', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.tiny}px`,
      color: '#6B7280',
      resolution: Math.max(3, window.devicePixelRatio), // High DPI for sharp text on mobile
    });
    desc.setOrigin(0.5);
    this.developerContainer.add(desc);
  }

  /**
   * Toggle disable leaderboard
   */
  private toggleDisableLeaderboard(): void {
    SettingsManager.toggleDisableLeaderboardSubmission();
    this.disableLeaderboardButton.setText(this.getDisableLeaderboardText());
  }

  /**
   * Get disable leaderboard button text
   */
  private getDisableLeaderboardText(): string {
    const disabled = SettingsManager.getDisableLeaderboardSubmission();
    return disabled ? 'ON' : 'OFF';
  }

  /**
   * Toggle show console
   */
  private toggleShowConsole(): void {
    SettingsManager.toggleShowJsConsole();
    this.showConsoleButton.setText(this.getShowConsoleText());
  }

  /**
   * Get show console button text
   */
  private getShowConsoleText(): string {
    const show = SettingsManager.getShowJsConsole();
    return show ? 'ON' : 'OFF';
  }

  /**
   * Create platform IDs toggle section
   */
  private createPlatformIdsToggleSection(centerX: number, y: number): void {
    // Section background with proper padding
    const sectionBg = this.add.rectangle(centerX, y + 45, 320, 130, 0x1a1a2a, 0.6);
    sectionBg.setStrokeStyle(2, parseInt(UI_COLORS.primary.replace('#', '0x')), 0.3);
    this.developerContainer.add(sectionBg);

    // Title
    const sectionTitle = this.add.text(centerX, y, 'Show Platform IDs', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.body}px`,
      color: '#D1D5DB',
      fontStyle: 'bold',
      resolution: Math.max(3, window.devicePixelRatio), // High DPI for sharp text on mobile
    });
    sectionTitle.setOrigin(0.5);
    this.developerContainer.add(sectionTitle);

    // Toggle button
    this.showPlatformIdsButton = this.createToggleButton(
      centerX,
      y + 40,
      this.getShowPlatformIdsText(),
      () => this.toggleShowPlatformIds()
    );
    this.developerContainer.add(this.showPlatformIdsButton);

    // Description
    const desc = this.add.text(centerX, y + 75, 'Display platform numbers (#123) during gameplay', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.tiny}px`,
      color: '#6B7280',
      resolution: Math.max(3, window.devicePixelRatio), // High DPI for sharp text on mobile
    });
    desc.setOrigin(0.5);
    this.developerContainer.add(desc);
  }

  /**
   * Toggle show platform IDs
   */
  private toggleShowPlatformIds(): void {
    SettingsManager.toggleShowPlatformIds();
    this.showPlatformIdsButton.setText(this.getShowPlatformIdsText());
  }

  /**
   * Get show platform IDs button text
   */
  private getShowPlatformIdsText(): string {
    const show = SettingsManager.getShowPlatformIds();
    return show ? 'ON' : 'OFF';
  }

  /**
   * Toggle show FPS counter
   */
  private toggleShowFpsCounter(): void {
    SettingsManager.toggleShowFpsCounter();
    this.showFpsCounterButton.setText(this.getShowFpsCounterText());
  }

  /**
   * Get show FPS counter button text
   */
  private getShowFpsCounterText(): string {
    const show = SettingsManager.getShowFpsCounter();
    return show ? 'ON' : 'OFF';
  }

  /**
   * Create enable teleport toggle section
   */
  private createEnableTeleportSection(centerX: number, y: number): void {
    // Section background with proper padding
    const sectionBg = this.add.rectangle(centerX, y + 45, 320, 130, 0x1a1a2a, 0.6);
    sectionBg.setStrokeStyle(2, parseInt(UI_COLORS.primary.replace('#', '0x')), 0.3);
    this.developerContainer.add(sectionBg);

    // Title
    const sectionTitle = this.add.text(centerX, y, 'Enable Teleport Button', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.body}px`,
      color: UI_COLORS.primary,
      fontStyle: 'bold',
      resolution: Math.max(3, window.devicePixelRatio), // High DPI for sharp text on mobile
    });
    sectionTitle.setOrigin(0.5);
    this.developerContainer.add(sectionTitle);

    // Toggle button
    this.enableTeleportButton = this.createToggleButton(
      centerX,
      y + 40,
      this.getEnableTeleportText(),
      () => this.toggleEnableTeleport()
    );
    this.developerContainer.add(this.enableTeleportButton);

    // Description
    const desc = this.add.text(
      centerX,
      y + 75,
      'Enable teleport button for dev testing (default: OFF)',
      {
        fontFamily: TYPOGRAPHY.fonts.josefin,
        fontSize: `${TYPOGRAPHY.sizes.caption}px`,
        color: '#6B7280',
        resolution: Math.max(3, window.devicePixelRatio), // High DPI for sharp text on mobile
      }
    );
    desc.setOrigin(0.5);
    this.developerContainer.add(desc);
  }

  /**
   * Toggle enable teleport
   */
  private toggleEnableTeleport(): void {
    SettingsManager.toggleEnableTeleport();
    this.enableTeleportButton.setText(this.getEnableTeleportText());
  }

  /**
   * Get enable teleport button text
   */
  private getEnableTeleportText(): string {
    const enabled = SettingsManager.getEnableTeleport();
    return enabled ? 'ON' : 'OFF';
  }

  /**
   * Create victory testing section with buttons to add/clear sample entries
   */
  private createVictoryTestingSection(centerX: number, y: number): void {
    // Section background with proper padding
    const sectionBg = this.add.rectangle(centerX, y + 60, 320, 160, 0x1a1a2a, 0.6);
    sectionBg.setStrokeStyle(2, parseInt(UI_COLORS.primary.replace('#', '0x')), 0.3);
    this.developerContainer.add(sectionBg);

    // Title
    const sectionTitle = this.add.text(centerX, y, 'Test Victory Entries', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.body}px`,
      color: UI_COLORS.primary,
      fontStyle: 'bold',
      resolution: Math.max(3, window.devicePixelRatio),
    });
    sectionTitle.setOrigin(0.5);
    this.developerContainer.add(sectionTitle);

    // Description
    const desc = this.add.text(centerX, y + 30, 'Add sample entries to test layout', {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.caption}px`,
      color: '#6B7280',
      resolution: Math.max(3, window.devicePixelRatio),
    });
    desc.setOrigin(0.5);
    this.developerContainer.add(desc);

    // Buttons row
    const buttonY = y + 70;
    const buttonSpacing = 75;
    const startX = centerX - buttonSpacing * 1.5;

    // Clear button
    const clearButton = this.createActionButton(startX, buttonY, 'Clear', () => {
      VictoryHistoryManager.clearHistory();
      this.showToast('Victory entries cleared');
    });
    this.developerContainer.add(clearButton);

    // Add 1 button
    const add1Button = this.createActionButton(startX + buttonSpacing, buttonY, 'Add 1', () => {
      VictoryHistoryManager.addSampleEntries(1);
      this.showToast('Added 1 sample entry');
    });
    this.developerContainer.add(add1Button);

    // Add 2 button
    const add2Button = this.createActionButton(startX + buttonSpacing * 2, buttonY, 'Add 2', () => {
      VictoryHistoryManager.addSampleEntries(2);
      this.showToast('Added 2 sample entries');
    });
    this.developerContainer.add(add2Button);

    // Add 5 button
    const add5Button = this.createActionButton(startX + buttonSpacing * 3, buttonY, 'Add 5', () => {
      VictoryHistoryManager.addSampleEntries(5);
      this.showToast('Added 5 sample entries');
    });
    this.developerContainer.add(add5Button);
  }

  /**
   * Create a small action button
   */
  private createActionButton(x: number, y: number, text: string, onClick: () => void): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);

    // Background
    const bg = this.add.rectangle(0, 0, 60, 40, parseInt(UI_COLORS.primary.replace('#', '0x')), 0.3);
    bg.setStrokeStyle(2, parseInt(UI_COLORS.primary.replace('#', '0x')), 0.8);
    bg.setInteractive({ useHandCursor: true });
    container.add(bg);

    // Label
    const label = this.add.text(0, 0, text, {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: '13px',
      color: '#FFFFFF',
      fontStyle: 'bold',
      resolution: Math.max(3, window.devicePixelRatio),
    });
    label.setOrigin(0.5);
    container.add(label);

    // Click handler
    bg.on('pointerdown', () => {
      onClick();
    });

    // Hover effects
    bg.on('pointerover', () => {
      bg.setFillStyle(parseInt(UI_COLORS.primary.replace('#', '0x')), 0.5);
    });

    bg.on('pointerout', () => {
      bg.setFillStyle(parseInt(UI_COLORS.primary.replace('#', '0x')), 0.3);
    });

    return container;
  }

  /**
   * Handle title tap for developer settings reveal
   */
  private handleTitleTap(): void {
    const now = Date.now();

    // Reset counter if too much time has passed
    if (now - this.tapTimer > this.TAP_TIMEOUT) {
      this.tapCount = 0;
    }

    this.tapCount++;
    this.tapTimer = now;

    // Check if threshold reached
    if (this.tapCount >= this.TAP_THRESHOLD) {
      // Toggle developer settings visibility
      const newState = SettingsManager.toggleDevSettingsVisible();
      this.developerContainer.setVisible(newState);

      // Update button texts to reflect auto-changed settings
      this.showFpsCounterButton.setText(this.getShowFpsCounterText());
      this.disableLeaderboardButton.setText(this.getDisableLeaderboardText());
      this.enableTeleportButton.setText(this.getEnableTeleportText());

      // Show toast notification
      this.showToast(newState ? 'Developer settings enabled' : 'Developer settings disabled');

      // Reset counter
      this.tapCount = 0;

      // Haptic feedback
      HapticManager.settingsToggle();
    }
  }

  /**
   * Show toast notification
   */
  private showToast(message: string): void {
    const centerX = this.cameras.main.centerX;
    const centerY = this.cameras.main.centerY;

    // Create toast background
    const toast = this.add.rectangle(centerX, centerY, 300, 60, 0x1a1a2a, 0.95);
    toast.setStrokeStyle(2, parseInt(UI_COLORS.primary.replace('#', '0x')), 0.5);
    toast.setDepth(Z_INDEX.modal + 100);
    toast.setScrollFactor(0);

    // Create toast text
    const toastText = this.add.text(centerX, centerY, message, {
      fontFamily: TYPOGRAPHY.fonts.josefin,
      fontSize: `${TYPOGRAPHY.sizes.body}px`,
      color: UI_COLORS.primary,
      fontStyle: 'bold',
      resolution: Math.max(3, window.devicePixelRatio),
    });
    toastText.setOrigin(0.5);
    toastText.setDepth(Z_INDEX.modal + 101);
    toastText.setScrollFactor(0);

    // Fade in
    toast.setAlpha(0);
    toastText.setAlpha(0);

    this.tweens.add({
      targets: [toast, toastText],
      alpha: 1,
      duration: 200,
      ease: 'Power2',
      onComplete: () => {
        // Wait then fade out
        this.time.delayedCall(2000, () => {
          this.tweens.add({
            targets: [toast, toastText],
            alpha: 0,
            duration: 200,
            ease: 'Power2',
            onComplete: () => {
              toast.destroy();
              toastText.destroy();
            },
          });
        });
      },
    });
  }

  /**
   * Cleanup on scene shutdown
   */
  private cleanup(): void {
    // Stop music test if playing
    if (this.isMusicTestPlaying) {
      SoundManager.stopMusic();
      this.isMusicTestPlaying = false;
    }

    // Remove gyroscope listener
    if (this.orientationHandler) {
      window.removeEventListener('deviceorientation', this.orientationHandler);
      this.orientationHandler = null;
    }
  }

  shutdown(): void {
    this.cleanup();
  }
}
